import React, { useState, useEffect, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import { pdf } from "@react-pdf/renderer";
import PDFDocument from "../components/PDFDocument";
import Navbar from "../components/Navbar";

export default function OrderList() {
    const [orderData, setOrderData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedDeliveryStatus, setSelectedDeliveryStatus] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [error, setError] = useState(null);

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const Ledcode = localStorage.getItem("Ledcode");
    const AgentCode = localStorage.getItem("AgentCode");
    const customerType = localStorage.getItem("CustomerType");

    const deliveryStatusOptions = [
        { value: "0", label: "Pending" },
        { value: "1", label: "Success" },
    ];

    const addOneDay = (date) => new Date(date.getTime() + 86400000);

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(
            2,
            "0"
        )}-${date.getFullYear()}`;
    };

    const fetchData = async (pageNum, reset = false) => {
        if (!Ledcode || loading || (!reset && !hasMore)) return;

        setLoading(true);
        setError(null);

        try {
            const url = new URL(`${apiBaseUrl}/OrderList/JAPAN_ACCESORIES`);
            url.searchParams.set("ledcode", Ledcode);
            url.searchParams.set("agentCode", AgentCode || "");
            url.searchParams.set("page", pageNum);
            url.searchParams.set("limit", 20);
            if (searchQuery) url.searchParams.set("customer", searchQuery);
            if (startDate) url.searchParams.set("startDate", startDate.toISOString().slice(0, 10));
            if (endDate) url.searchParams.set("endDate", endDate.toISOString().slice(0, 10));
            if (selectedCustomer) url.searchParams.set("customer", selectedCustomer.value);
            if (selectedDeliveryStatus) url.searchParams.set("deliveryStatus", selectedDeliveryStatus.value);

            const response = await fetch(url.toString());
            if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
            let data = await response.json();

            data = data.sort((a, b) => a.InvoiceNo - b.InvoiceNo);

            setOrderData((prev) => (reset ? data : [...prev, ...data]));
            // console.log(orderData);

            if (pageNum === 1) {
                const uniqueCustomers = [
                    ...new Set(
                        data
                            .map(({ Customer }) => Customer)
                            .filter((c) => c && c !== "Unknown" && !c.startsWith("Customer "))
                    ),
                ];
                setCustomerOptions(uniqueCustomers.map((c) => ({ value: c, label: c })));
            }

            setHasMore(data.length === 20);
            setPage(pageNum + 1);
        } catch (err) {
            console.error(err);
            setError("Failed to fetch orders. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!Ledcode) return;

        const delayDebounce = setTimeout(() => {
            setOrderData([]);
            setPage(1);
            setHasMore(true);
            fetchData(1, true);
        }, 400);

        return () => clearTimeout(delayDebounce);
    }, [searchQuery, startDate, endDate, selectedCustomer, selectedDeliveryStatus, Ledcode, AgentCode, apiBaseUrl]);

    const scrollRef = useRef(null);
    const scrollTimeoutRef = useRef(null);

    const handleScroll = (e) => {
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

        scrollTimeoutRef.current = setTimeout(() => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            if (scrollHeight - scrollTop <= clientHeight + 50 && !loading && hasMore) {
                fetchData(page);
            }
        }, 150);
    };

    const filteredOrderData = useMemo(() => {
        return orderData.filter(({ Customer, DDate, DeliveryStatus }) => {
            const orderDate = DDate ? new Date(DDate) : null;

            // 🔍 Search filter (only when no specific customer is chosen)
            const matchesSearch =
                !selectedCustomer &&
                Customer &&
                typeof Customer === "string" &&
                Customer.toLowerCase().includes(searchQuery.toLowerCase());

            // 📅 Date filter
            const inDateRange =
                (!startDate || (orderDate && orderDate >= startDate)) &&
                (!endDate || (orderDate && orderDate <= addOneDay(endDate)));

            // 🚚 Delivery Status filter
            const matchesStatus =
                !selectedDeliveryStatus ||
                (DeliveryStatus !== null &&
                    DeliveryStatus !== undefined &&
                    DeliveryStatus.toString() === selectedDeliveryStatus.value);

            // 👤 Strict Customer filter (only exact match if selected)
            const matchesCustomer =
                !selectedCustomer ||
                (Customer && Customer.trim().toLowerCase() === selectedCustomer.value.trim().toLowerCase());

            return (matchesSearch || selectedCustomer) && inDateRange && matchesStatus && matchesCustomer;
        });
    }, [orderData, searchQuery, startDate, endDate, selectedDeliveryStatus, selectedCustomer]);

    const filteredTotalSum = useMemo(
        () => filteredOrderData.reduce((sum, { Total }) => sum + (Total || 0), 0),
        [filteredOrderData]
    );
    const filteredProfitSum = useMemo(
        () => filteredOrderData.reduce((sum, { Profit }) => sum + (Profit || 0), 0),
        [filteredOrderData]
    );

    const exportToExcel = () => {
        let csv = "Date,Entry No,Customer,Total";
        if (customerType !== "DEALER") csv += ",Profit";
        csv += "\n";

        csv += filteredOrderData
            .map(({ DDate, InvoiceNo, Customer, Total, Profit }) => {
                const row = [formatDate(DDate), InvoiceNo || "", Customer || "", (Total || 0).toFixed(2)];
                if (customerType !== "DEALER") row.push((Profit || 0).toFixed(2));
                return row.join(",");
            })
            .join("\n");

        csv += `\nTotal,${filteredOrderData.length},,${filteredTotalSum.toFixed(2)}`;
        if (customerType !== "DEALER") csv += `,${filteredProfitSum.toFixed(2)}`;

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "order_list.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownload = async () => {
        setPdfLoading(true);
        setError(null);
        try {
            const blob = await pdf(
                <PDFDocument
                    data={filteredOrderData}
                    totalSum={filteredTotalSum}
                    profitSum={filteredProfitSum}
                    formatDate={formatDate}
                    customerType={customerType}
                />
            ).toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `OrderList-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            setError("Failed to generate PDF. Please try again.");
        } finally {
            setPdfLoading(false);
        }
    };

    const clearFilters = () => {
        setSearchQuery("");
        setStartDate(null);
        setEndDate(null);
        setSelectedCustomer(null);
        setSelectedDeliveryStatus(null);
    };

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="sticky top-0 z-50">
                <Navbar />
            </div>

            <div className="container mx-auto p-6 space-y-6">
                {error && <div className="bg-red-100 text-red-700 p-4 rounded-md text-center font-semibold">{error}</div>}

                <div className="flex flex-col items-center space-y-4">
                    {/* <div className="flex w-full gap-4 md:hidden items-center justify-center">
                        <button onClick={handleDownload} title="Export to PDF" className="hover:opacity-80">
                            <img className="h-8" src="https://i.postimg.cc/TYcDhjmn/pdf-image.png" alt="PDF" />
                        </button>
                        <h1 className="text-2xl font-bold text-gray-800">Order List</h1>
                        <button onClick={exportToExcel} title="Export to Excel" className="hover:opacity-80">
                            <img className="h-8" src="https://i.postimg.cc/Kc9144S4/excel.png" alt="Excel" />
                        </button>
                    </div> */}
                    <h1 className="text-2xl font-bold text-gray-800">Order List</h1>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <DatePicker
                            selected={startDate}
                            onChange={setStartDate}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            placeholderText="Start Date"
                            dateFormat="dd-MM-yyyy"
                            className="input border border-[#ABABAB] input-sm w-48 px-4 py-2 rounded-md h-10"
                            maxDate={endDate}
                            isClearable
                        />
                        <DatePicker
                            selected={endDate}
                            onChange={setEndDate}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            placeholderText="End Date"
                            dateFormat="dd-MM-yyyy"
                            className="input  border border-[#ABABAB] input-sm w-48 px-4 py-2 rounded-md h-10"
                            minDate={startDate}
                            isClearable
                        />
                        <div className="md:flex gap-4 hidden">
                            <button onClick={handleDownload} title="Export to PDF" className="hover:opacity-80">
                                <img className="h-8" src="https://i.postimg.cc/TYcDhjmn/pdf-image.png" alt="PDF" />
                            </button>
                            <button onClick={exportToExcel} title="Export to Excel" className="hover:opacity-80">
                                <img className="h-8" src="https://i.postimg.cc/Kc9144S4/excel.png" alt="Excel" />
                            </button>
                        </div>
                        <div className="flex w-full items-center justify-center gap-4 md:hidden">
                            <button onClick={handleDownload} title="Export to PDF" className="hover:opacity-80">
                                <img className="h-8" src="https://i.postimg.cc/TYcDhjmn/pdf-image.png" alt="PDF" />
                            </button>
                            <button onClick={exportToExcel} title="Export to Excel" className="hover:opacity-80">
                                <img className="h-8" src="https://i.postimg.cc/Kc9144S4/excel.png" alt="Excel" />
                            </button>
                        </div>

                        <Select
                            options={customerOptions}
                            onChange={setSelectedCustomer}
                            placeholder="Select Customer"
                            isClearable
                            className="w-48"
                        />
                        <Select
                            options={deliveryStatusOptions}
                            onChange={setSelectedDeliveryStatus}
                            placeholder="Delivery Status"
                            isClearable
                            className="w-48"
                        />
                        {/* <button
                            onClick={clearFilters}
                            className="btn btn-sm btn-outline border-gray-300 hover:bg-gray-200 px-4 py-2 rounded-md"
                        >
                            Clear Filters
                        </button> */}
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg shadow-md p-2">
                    <div
                        ref={scrollRef}
                        onScroll={handleScroll}
                        className="bg-white rounded-lg max-h-[500px] overflow-y-auto"
                    >
                        <div className="px-4 overflow-x-auto">
                            {" "}
                            {/* ✅ Enable horizontal scroll */}
                            <div className="min-w-[900px]">
                                {" "}
                                {/* ✅ Prevent columns from shrinking too much */}
                                {/* Header */}
                                <div className="grid grid-cols-7 border-b border-[#e5e7eb] bg-white sticky top-0 text-lg font-bold text-gray-500 items-center text-center h-14">
                                    <div className="text-[#ABABAB]">Date</div>
                                    <div className="text-[#ABABAB]">Entry No</div>
                                    <div className="col-span-2 text-[#ABABAB]">Customer</div>
                                    <div className="text-[#ABABAB]">Total</div>
                                    <div className="text-[#ABABAB]">
                                        {customerType !== "DEALER" ? "Profit" : "Delivery Status"}
                                    </div>
                                    <div className="text-[#ABABAB]">Delivery Status</div>
                                </div>
                                {/* Body */}
                                {loading ? (
                                    <div className="flex items-center justify-center p-6">
                                        <div className="flex items-center space-x-2 text-gray-600">
                                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-600"></div>
                                            <span>Loading...</span>
                                        </div>
                                    </div>
                                ) : filteredOrderData.length === 0 ? (
                                    <div className="text-center p-6 text-gray-500 font-semibold">No records found</div>
                                ) : (
                                    filteredOrderData.map(
                                        ({ DDate, InvoiceNo, Customer, Total, Profit, DeliveryStatus }, i) => (
                                            <div
                                                key={i}
                                                className={`grid grid-cols-7 gap-4 items-center p-3 ${
                                                    i % 2 === 0 ? "bg-white" : "bg-gray-50"
                                                } rounded-md`}
                                                title={`${Customer || "Unknown"} - ${formatDate(DDate)}`}
                                            >
                                                <div className="text-center font-semibold">{formatDate(DDate)}</div>
                                                <div className="text-center font-semibold">{InvoiceNo || "-"}</div>
                                                <div className="col-span-2 text-left font-semibold">{Customer || "-"}</div>
                                                <div className="text-right font-semibold">{(Total || 0).toFixed(2)}</div>
                                                {customerType !== "DEALER" ? (
                                                    <div className="text-right font-semibold">
                                                        {(Profit || 0).toFixed(2)}
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="text-center font-semibold"
                                                        style={{
                                                            color:
                                                                DeliveryStatus === 1
                                                                    ? "green"
                                                                    : DeliveryStatus === 0
                                                                    ? "red"
                                                                    : "inherit",
                                                            fontWeight:
                                                                DeliveryStatus === 1 || DeliveryStatus === 0
                                                                    ? "bold"
                                                                    : "normal",
                                                        }}
                                                    >
                                                        {DeliveryStatus === 0
                                                            ? "Pending"
                                                            : DeliveryStatus === 1
                                                            ? "Success"
                                                            : DeliveryStatus || "-"}
                                                    </div>
                                                )}
                                                <div
                                                    className="text-center font-semibold"
                                                    style={{
                                                        color:
                                                            String(DeliveryStatus) === "1"
                                                                ? "green"
                                                                : String(DeliveryStatus) === "0"
                                                                ? "red"
                                                                : "inherit",
                                                        fontWeight:
                                                            String(DeliveryStatus) === "1" || String(DeliveryStatus) === "0"
                                                                ? "bold"
                                                                : "normal",
                                                    }}
                                                >
                                                    {String(DeliveryStatus) === "0"
                                                        ? "Pending"
                                                        : String(DeliveryStatus) === "1"
                                                        ? "Success"
                                                        : "-"}
                                                </div>
                                            </div>
                                        )
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {pdfLoading && (
                    <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
                        <div className="flex items-center space-x-2 text-gray-600 font-semibold">
                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-600"></div>
                            <span>Generating PDF...</span>
                        </div>
                    </div>
                )}

                <div className="text-right font-bold text-gray-700">
                    Total Entries: {filteredOrderData.length} | Total Sum: {filteredTotalSum.toFixed(2)}{" "}
                    {customerType !== "DEALER" && `| Total Profit: ${filteredProfitSum.toFixed(2)}`}
                </div>
            </div>
        </div>
    );
}
